import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { CountdownBar } from '../components/Countdown';
import PaywallBanner from '../components/PaywallBanner';
import { useGames, ensureGames, refreshGames as refreshSharedGames } from '../gamesStore';

const TEAM_COLORS = {
  ARI:'#97233F',ATL:'#A71930',BAL:'#241773',BUF:'#00338D',CAR:'#0085CA',
  CHI:'#0B162A',CIN:'#FB4F14',CLE:'#311D00',DAL:'#003594',DEN:'#FB4F14',
  DET:'#0076B6',GB:'#203731',HOU:'#03202F',IND:'#002C5F',JAX:'#006778',
  KC:'#E31837',LAC:'#0080C6',LAR:'#003594',LV:'#A5ACAF',MIA:'#008E97',
  MIN:'#4F2683',NE:'#002244',NO:'#D3BC8D',NYG:'#0B2265',NYJ:'#125740',
  PHI:'#004C54',PIT:'#FFB612',SEA:'#002244',SF:'#AA0000',TB:'#D50A0A',
  TEN:'#0C2340',WAS:'#773141'
};

function logoUrl(abbr) {
  return 'https://a.espncdn.com/i/teamlogos/nfl/500/' + abbr.toLowerCase() + '.png';
}

function TeamLogo({ abbr, size, dim }) {
  const [ok, setOk] = useState(true);
  return ok ? (
    <img src={logoUrl(abbr)} alt={abbr} width={size} height={size}
      style={{ objectFit:'contain', opacity: dim ? 0.25 : 1, transition:'opacity 0.2s', display:'block' }}
      onError={() => setOk(false)} />
  ) : (
    <div style={{ width:size, height:size, borderRadius:8,
      background:(TEAM_COLORS[abbr]||'#333')+'33',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:size*0.28, fontWeight:700, color:'#fff',
      fontFamily:"'Oswald',sans-serif", opacity: dim ? 0.3 : 1
    }}>{abbr}</div>
  );
}

// Is this specific game locked? (5 min before kickoff or week manually locked)
function isGameLockedNow(game, weekLocked) {
  if (weekLocked) return true;
  if (!game.game_date_iso) return game.status !== 'scheduled';
  return Date.now() >= new Date(game.game_date_iso).getTime() - 5 * 60 * 1000;
}

function getStatusStyle(status, gameLocked, hasPick) {
  if (status === 'final') return { bg:'rgba(76,175,80,0.15)', color:'#4caf50' };
  if (status === 'in_progress') return { bg:'rgba(213,10,10,0.18)', color:'#ff6060' };
  if (gameLocked && !hasPick) return { bg:'rgba(213,10,10,0.15)', color:'#D50A0A' };
  if (gameLocked) return { bg:'rgba(255,160,0,0.12)', color:'#ffa000' };
  return { bg:'rgba(255,255,255,0.07)', color:'#667' };
}
function getStatusLabel(status, gameLocked, hasPick) {
  if (status === 'final') return 'FINAL';
  if (status === 'in_progress') return '● LIVE';
  if (gameLocked && !hasPick) return '🚫 MISSED';
  if (gameLocked) return '🔒 LOCKED';
  return 'UPCOMING';
}
function groupByDay(games) {
  const g = {};
  games.forEach(gm => { const d = gm.game_date||'TBD'; if (!g[d]) g[d]=[]; g[d].push(gm); });
  return g;
}

/**
 * Unsaved work, and how not to lose it.
 *
 * Picks only count once they're saved, and the page is deliberately save-based
 * — clearing a pick is a change you confirm, not an instant write. The cost of
 * that choice is that an edit sitting on screen is worth nothing, and the old
 * page said so nowhere: navigate away, refresh, or switch sheets and it was
 * gone silently. Someone who cleared a pick by accident, wandered off, and came
 * back would find the pick still there and assume it had saved.
 *
 * So the working set is mirrored to localStorage per (entry, week) the moment
 * it differs from what the server holds, restored on the way back in, and
 * called out in a banner that gets louder as kickoff approaches.
 */
const draftKey = (entryId, weekId) => `rp_draft:${entryId}:${weekId}`;

function samePick(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (a.team || null) === (b.team || null)
    && (a.confidence || null) === (b.confidence || null)
    && (a.comment || '') === (b.comment || '')
    && ((a.tiebreaker ?? null) === (b.tiebreaker ?? null));
}

/** What's changed since the last save — counted, so the banner can be specific. */
function diffPicks(current, saved) {
  const ids = new Set([...Object.keys(current || {}), ...Object.keys(saved || {})]);
  let changed = 0, cleared = 0;
  for (const id of ids) {
    const c = (current || {})[id], s = (saved || {})[id];
    if (samePick(c, s)) continue;
    if (s && s.team && !(c && c.team)) cleared++;
    else changed++;
  }
  return { changed, cleared, dirty: changed + cleared > 0 };
}

/**
 * "Your pick is up 7" — the only question anyone actually has about a game in
 * progress. Derived from data already on the card, so it costs nothing.
 */
function livePickState(game, myTeam) {
  if (!myTeam || game.status !== 'in_progress') return null;
  const mine = myTeam === game.home_abbr ? game.home_score : game.away_score;
  const theirs = myTeam === game.home_abbr ? game.away_score : game.home_score;
  const d = mine - theirs;
  if (d > 0) return { text: `Your pick is up ${d}`, color: '#4caf50' };
  if (d < 0) return { text: `Your pick is trailing by ${-d}`, color: '#ff6666' };
  return { text: 'Your pick is tied', color: '#ffa000' };
}

/**
 * "You and 6 others took KC · 2 took DEN" — phrased around the reader, because
 * being with or against the room is the point. Falls back to a plain count when
 * they didn't pick the game at all.
 */
function splitLine(split, myTeam) {
  if (!split || !split.total) return null;
  const entries = Object.entries(split.counts).sort((a, b) => b[1] - a[1]);
  const parts = entries.map(([team, n]) => {
    if (team === myTeam) {
      const others = n - 1;
      return others === 0
        ? `only you took ${team}`
        : `you and ${others} other${others === 1 ? '' : 's'} took ${team}`;
    }
    return `${n} took ${team}`;
  });
  return parts.join(' · ');
}

/**
 * The background of a side you've picked.
 *
 * A neutral blue wash layered OVER the team tint. Relying on the team's own
 * colour alone is what made this unreadable: Bears (#0B162A) and Browns
 * (#311D00) are both near-black on a #0d1525 card, so a 16%-alpha wash of them
 * was indistinguishable from not picking at all.
 */
function pickedBackground(teamColor) {
  return `linear-gradient(rgba(127,179,255,0.16), rgba(127,179,255,0.07)), ` +
         `linear-gradient(${teamColor}55, ${teamColor}33)`;
}

// Shared by both cards. The tiebreaker draws from its own block further down —
// when this treatment lived inside the game loop, the tiebreaker silently kept
// the old flat highlight and was the one game you couldn't read your pick on.
const PICK_RING = 'rgba(127,179,255,0.85)';

/*
 * What is worth knowing before picking a game: the line, who the model likes,
 * recent form, and who might not play.
 *
 * A component rather than inline markup because it is needed in two places that
 * look nothing alike — the ordinary game card and the tiebreaker block — and the
 * tiebreaker is the one that most needs it, since the over/under IS the market's
 * combined-score prediction and that is the number being asked for there.
 *
 * Renders nothing at all when there is no briefing, or when the game can no
 * longer be picked. A briefing is an aid to a decision; once a game is live or
 * final there is no decision left to aid, and it would only be pushing the score
 * further down the card.
 */
function BriefingStrip({ game, brief, show, open, onToggle }) {
  if (!show || !brief) return null;

  const away = brief.injuries?.away || [];
  const home = brief.injuries?.home || [];
  // The collapsed line is assembled on the server alongside the summary, so the
  // wording sits with the data and is covered by the same suite.
  const summary = brief.headline;

  const injuryLine = (list, abbr) => {
    if (!list.length) return null;
    return (
      <div style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#7fb3ff', letterSpacing: 0.5 }}>{abbr}</span>
        {list.map((x, i) => (
          <div key={i} style={{ fontSize: 12.5, color: '#99a', marginTop: 2 }}>
            {x.name}{x.pos ? ' (' + x.pos + ')' : ''}: <span style={{
              color: x.status === 'Out' || x.status === 'Injured Reserve' ? '#ff8888' : '#ffa726'
            }}>{x.status}</span>{x.detail ? ', ' + String(x.detail).toLowerCase() : ''}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', background: 'rgba(0,0,0,0.18)' }}>
      {/* stopPropagation, because on a game card this button sits inside
          something whose whole job is tap-to-pick. Without it, reading the
          briefing would make a pick. */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 16px', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left', color: '#778', fontSize: 12.5,
          fontWeight: 600, minHeight: 44
        }}
      >
        <span style={{ color: '#7fb3ff', flexShrink: 0 }}>{open ? '▾' : '▸'} Briefing</span>
        {summary && (
          <span style={{ color: '#667', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {summary}
          </span>
        )}
      </button>

      {open && (
        <div style={{ padding: '2px 16px 14px' }}>
          {/* The same facts as a couple of sentences, built on the server.
              This REPLACED a "SEA favoured at 63%" line and a "Last 5:" line
              rather than joining them: three ways of saying the market likes
              Seattle is not three times the information, it is a panel nobody
              finishes reading. */}
          {brief.summary && (
            <div style={{ fontSize: 13, color: '#ccd', lineHeight: 1.5, marginBottom: 8 }}>
              {brief.summary}
            </div>
          )}
          {/* The first question a football person asks on seeing a spread is
              whether they are picking against it. Until today there were no
              lines anywhere in this app, and the first member to see one asked
              exactly that within the hour. Answering it beside the number costs
              a line and saves the same conversation twenty times. */}
          {(brief.spread || brief.overUnder != null) && (
            <div style={{
              fontSize: 12.5, color: '#7fb3ff', lineHeight: 1.45, marginBottom: 12,
              padding: '7px 10px', borderRadius: 8,
              background: 'rgba(127,179,255,0.08)',
              border: '1px solid rgba(127,179,255,0.22)'
            }}>
              Pick the winner, straight up. The line and total are here to help you decide and do not affect scoring.
            </div>
          )}
          {(away.length || home.length) ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#556', letterSpacing: 0.8, marginBottom: 6 }}>WHO MIGHT NOT PLAY</div>
              {injuryLine(away, game.away_abbr)}
              {injuryLine(home, game.home_abbr)}
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: '#667' }}>Nobody of note on either injury report.</div>
          )}
          {/* Injury reports go stale fast, and a Wednesday report read on Sunday
              is worse than none, so the age travels with the data. */}
          {brief.fetchedAt && (
            <div style={{ fontSize: 11, color: '#445', marginTop: 10 }}>
              Updated {new Date(brief.fetchedAt.replace(' ', 'T') + 'Z')
                .toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}. Not betting advice.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PickChip({ side }) {
  return (
    <span style={{
      position:'absolute', top:8, [side === 'away' ? 'right' : 'left']: 8,
      display:'inline-flex', alignItems:'center', gap:3,
      padding:'2px 7px', borderRadius:10, background:'rgba(127,179,255,0.18)',
      border:'1px solid ' + PICK_RING, color:'#bcd9ff',
      fontSize:10, fontWeight:800, letterSpacing:0.6
    }}>✓ PICK</span>
  );
}

const CONFIDENCE = ['low','medium','high'];
const CONF_LABEL = { low:'Low', medium:'Medium', high:'High' };
const CONF_COLOR = { low:'#778', medium:'#ffa000', high:'#4caf50' };

export default function PicksPage({ user, membership, onNavigate }) {
  const [games, setGames] = useState([]);
  const [week, setWeek] = useState(null);
  const [myPicks, setMyPicks] = useState({});
  const [savedPicks, setSavedPicks] = useState({});
  const [tbScore, setTbScore] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  // A refusal is not a success, and must not be shown in the success colour.
  const [saveWarn, setSaveWarn] = useState(false);
  // Which card just refused a tap, and what to say on it. Shown on the card
  // itself rather than by the save button, which may be off screen.
  const [lockNote, setLockNote] = useState(null);
  const [error, setError] = useState('');
  // One sheet per pool. `entries` is every seat the player holds; `entryId` is
  // the one on screen. Whether they may save is now a property of that seat,
  // not of the account — paying for one pool no longer unlocks the others.
  const [entries, setEntries] = useState([]);
  const [entryId, setEntryId] = useState(null);
  const [copying, setCopying] = useState(false);
  // How the pool split each revealed game, from the server (it owns the reveal
  // rule). Keyed by game id; absent for games whose picks are still private.
  const [splits, setSplits] = useState({});
  const [briefings, setBriefings] = useState({});
  // Which briefings are open, by game id. Collapsed by default and deliberately
  // not remembered: sixteen expanded briefings is a wall of text on a phone, and
  // that is the state you would come back to.
  const [openBriefs, setOpenBriefs] = useState({});
  // Which entry the loaded picks actually belong to. Distinct from `entryId`,
  // which is the UI's selection and changes a render before the picks arrive.
  // Draft persistence keys off THIS: keying off the selection meant the moment
  // between "entry chosen" and "picks fetched" looked like an empty, unmodified
  // sheet, and the effect below helpfully deleted the draft it was about to
  // restore. Null while a load is in flight, which parks persistence entirely.
  const [picksEntryId, setPicksEntryId] = useState(null);

  const active = entries.find(e => e.entry_id === entryId) || null;
  const locked = active
    ? active.canSubmit === false
    : !!(membership && membership.canSubmitPicks === false);

  // Refreshing means asking the shared copy to update; every surface sees the
  // result at the same instant, which is the whole point.
  const refreshGames = useCallback(() => refreshSharedGames(), []);

  /**
   * Load one sheet from the server, then lay any unsaved draft back on top.
   * `savedPicks` always mirrors the server, so the diff between the two is
   * exactly "what hasn't been saved" — which is what the banner reports and
   * what the next save sends.
   */
  const loadPicks = useCallback(async (id, weekId) => {
    setPicksEntryId(null);          // parks the persist effect during the fetch
    const pr = await API.get('/picks/mine' + (id ? `?entry_id=${id}` : ''));
    setSplits(pr.data.splits || {});
    const pm = {};
    (pr.data.picks || []).forEach(p => {
      pm[p.game_id] = { team: p.picked_team, tiebreaker: p.tiebreaker_score, confidence: p.confidence||null, comment: p.pick_comment||'' };
    });
    setSavedPicks(pm);

    let working = pm;
    if (id && weekId) {
      try {
        const raw = localStorage.getItem(draftKey(id, weekId));
        if (raw) {
          const draft = JSON.parse(raw);
          // A draft that now matches the server is stale — someone saved from
          // another device, or the same edits came back. Drop it rather than
          // claiming there's unsaved work.
          if (diffPicks(draft, pm).dirty) working = draft;
          else localStorage.removeItem(draftKey(id, weekId));
        }
      } catch (e) { /* unreadable draft is not worth failing the page over */ }
    }
    // Set together so no render sees picks from one entry tagged as another's.
    setMyPicks(working);
    setPicksEntryId(id ?? null);
    return working;
  }, []);

  /**
   * Re-read the sheet list.
   *
   * It carries this week's pick count for every seat, and it was only ever
   * fetched on mount. So after saving, the sheet you had just filled in still
   * read "not started" underneath the button you pressed, and the only way to
   * see your own picks counted was to leave the page and come back. Anything
   * that changes what a seat holds has to ask for it again.
   *
   * Deliberately does not touch `entryId`: this runs while somebody is looking
   * at a sheet, and moving them off it would be worse than a stale count.
   */
  const refreshEntries = useCallback(async () => {
    try {
      const er = await API.get('/entries/mine');
      setEntries(er.data.entries || []);
    } catch (e) { /* a readout that failed to refresh is not worth an error */ }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [gr, er] = await Promise.all([ensureGames(), API.get('/entries/mine')]);
      setGames(gr.games || []);
      setWeek(gr.week);
      const list = er.data.entries || [];
      setEntries(list);
      // Open on a seat they can actually play, so the page doesn't greet a
      // paid-up member with a locked sheet just because it sorted first.
      const first = list.find(e => e.canSubmit) || list[0];
      const id = first ? first.entry_id : null;
      setEntryId(id);
      const pm = await loadPicks(id, gr.week?.id);
      const tb = (gr.games||[]).find(g => g.is_tiebreaker);
      if (tb && pm[tb.id]?.tiebreaker) setTbScore(String(pm[tb.id].tiebreaker));
      else setTbScore('');
      /* Briefings are a nicety, so they are fetched AFTER the sheet is on screen
         and their failure is swallowed. Making the page that takes picks wait on
         injury data, or fail without it, would be trading the thing the app is
         for a thing it merely offers. */
      try {
        const br = await API.get('/games/briefings'
          + (gr.week?.id ? '?week_id=' + gr.week.id : ''));
        setBriefings(br.data?.briefings || {});
      } catch (e) { /* no briefings, no strip */ }
    } catch (e) { setError('Failed to load games.'); }
    finally { setLoading(false); }
  }, [loadPicks]);

  const switchEntry = async (id) => {
    if (id === entryId) return;
    setEntryId(id); setSaveMsg('');
    // Drafts are per sheet, so switching away and back brings yours with you.
    const pm = await loadPicks(id, week?.id);
    const tb = games.find(g => g.is_tiebreaker);
    setTbScore(tb && pm[tb.id]?.tiebreaker ? String(pm[tb.id].tiebreaker) : '');
  };

  // The answer to "three pools means three identical sheets a week".
  const copyFrom = async (fromId) => {
    setCopying(true); setSaveMsg('');
    try {
      const r = await API.post(`/entries/${entryId}/copy`, { from_entry_id: fromId });
      const pm = await loadPicks(entryId, week?.id);
      const tb = games.find(g => g.is_tiebreaker);
      setTbScore(tb && pm[tb.id]?.tiebreaker ? String(pm[tb.id].tiebreaker) : '');
      setSaveMsg(r.data?.message || 'Picks copied!');
      setTimeout(() => setSaveMsg(''), 3000);
      // A copy fills a sheet just as a save does, so the list has to be re-read
      // for the same reason.
      refreshEntries();
    } catch (e) {
      setSaveMsg(e.response?.data?.error || 'Could not copy those picks.');
    } finally { setCopying(false); }
  };

  useEffect(() => { loadData(); }, [loadData]);

  // Scores and statuses come from the shared copy — one timer for the whole app,
  // so this page and the ticker can never show different clocks. The page's own
  // `games` mirrors it, because everything below reads picks off that list.
  const shared = useGames();
  useEffect(() => {
    if (!shared.loaded) return;
    setGames(shared.games);
    setWeek(shared.week);
  }, [shared]);

  const pending = diffPicks(myPicks, savedPicks);

  // Mirror unsaved work to storage so leaving the page — by tab, by refresh, by
  // closing the browser — doesn't throw it away. Cleared the moment the working
  // set matches the server again, so a saved sheet never looks unsaved.
  useEffect(() => {
    if (picksEntryId == null || !week) return;
    const key = draftKey(picksEntryId, week.id);
    try {
      if (pending.dirty) localStorage.setItem(key, JSON.stringify(myPicks));
      else localStorage.removeItem(key);
    } catch (e) { /* storage full or blocked — the banner still warns */ }
  }, [myPicks, savedPicks, picksEntryId, week, pending.dirty]);

  // The browser's own "leave site?" prompt. Only while there's something to
  // lose, or it becomes noise people click through without reading.
  useEffect(() => {
    if (!pending.dirty) return;
    const onLeave = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [pending.dirty]);

  // Minutes until picks lock, for deciding how loud to be.
  const minsToLock = week && week.lock_at
    ? (new Date(week.lock_at).getTime() - Date.now()) / 60000
    : null;
  const urgent = pending.dirty && minsToLock != null && minsToLock > 0 && minsToLock <= 120;

  /**
   * Tapping a team.
   *
   * Two things were wrong here. It returned silently when the game was shut, so
   * tapping a locked matchup did nothing at all and never said why — you were
   * left to work out whether the app was broken or the game was closed.
   *
   * And the guard read the WEEK lock, not this game's. A game inside its last
   * five minutes is still `scheduled` and the week isn't locked, so it stayed
   * tappable: the pick moved on screen, and the server quietly refused it on
   * save. Same class of gap as the save bug — the screen said one thing and the
   * database another.
   */
  const pickTeam = (gameId, team, gameLocked, status) => {
    if (gameLocked || status !== 'scheduled') {
      setLockNote({
        gameId,
        text: status === 'final' ? 'This game is over. Picks are final.'
            : status === 'in_progress' ? 'This game has started. Picks lock at kickoff and can\'t be changed.'
            : 'This game locks five minutes before kickoff, too late to change it.'
      });
      return;
    }
    setLockNote(null);
    setMyPicks(prev => ({ ...prev, [gameId]: { ...(prev[gameId]||{}), team } }));
  };
  // Clear the note on its own, so it doesn't sit there after they've moved on.
  useEffect(() => {
    if (!lockNote) return;
    const t = setTimeout(() => setLockNote(null), 6000);
    return () => clearTimeout(t);
  }, [lockNote]);

  const setConfidence = (gameId, confidence) => {
    setMyPicks(prev => ({ ...prev, [gameId]: { ...(prev[gameId]||{}), confidence } }));
  };
  const setComment = (gameId, comment) => {
    setMyPicks(prev => ({ ...prev, [gameId]: { ...(prev[gameId]||{}), comment } }));
  };
  // Sit a game out. Only drops it from the working set — the removal is sent
  // with the next save, so clearing one pick and changing another stays a single
  // save rather than a silent write followed by an explicit one.
  const clearPick = (gameId) => {
    setMyPicks(prev => { const next = { ...prev }; delete next[gameId]; return next; });
  };

  const savePicks = async () => {
    setSaving(true); setSaveMsg('');
    try {
      const payload = Object.entries(myPicks)
        .filter(([,v]) => v.team)
        .map(([gid, v]) => {
          const g = games.find(x => x.id === parseInt(gid));
          return {
            game_id: parseInt(gid),
            picked_team: v.team,
            // Not `parseInt(tbScore) || null` — that turned a predicted 0 into
            // "no prediction", which is exactly the state the server now rejects.
            tiebreaker_score: g?.is_tiebreaker
              ? (String(tbScore).trim() === '' ? null : parseInt(tbScore, 10))
              : null,
            confidence: v.confidence || null,
            pick_comment: v.comment || null
          };
        });
      // Anything that was saved with a team and no longer has one has been
      // cleared. Derived from the diff rather than tracked separately, so the
      // two can't drift apart.
      const remove = Object.keys(savedPicks)
        .filter(gid => savedPicks[gid]?.team && !myPicks[gid]?.team)
        .map(gid => parseInt(gid));
      const r = await API.post('/picks', { picks: payload, remove, entry_id: entryId });

      // Games the server refused because they had already locked. Roll those
      // back to what it actually holds, rather than leaving the screen showing
      // a change that didn't happen — marking them saved was how a member ended
      // up believing a pick they never made.
      const refused = r.data?.refused || [];
      const refusedIds = new Set(refused.map(x => x.game_id));
      const accepted = { ...myPicks };
      for (const gid of refusedIds) {
        if (savedPicks[gid]) accepted[gid] = savedPicks[gid];
        else delete accepted[gid];
      }
      setMyPicks(accepted);
      setSavedPicks(accepted);

      // Saved: there is no longer unsaved work to restore.
      try { if (entryId && week) localStorage.removeItem(draftKey(entryId, week.id)); } catch (e) {}
      // The server saves what it can and names what it skipped, so a member with
      // one paid pool and one unpaid one still gets their picks in.
      const skipped = r.data?.skippedPools || [];
      const cleared = r.data?.removed || 0;
      const clearedNote = cleared ? ` ${cleared} pick${cleared === 1 ? '' : 's'} cleared.` : '';

      if (refused.length) {
        // The server's wording already names the games; it knows which ones and
        // whether anything else got through.
        setSaveMsg(r.data.message);
        setSaveWarn(true);
        // Longer, because it's telling them something they need to act on —
        // and pull the board so those games show as locked.
        setTimeout(() => { setSaveMsg(''); setSaveWarn(false); }, 9000);
        refreshGames();
        refreshEntries();
      } else {
        setSaveWarn(false);
        // A plain "Picks saved!" is a half-truth for somebody with no pool: it
        // is saved, and it is scoring in nothing, and the second half used to go
        // unsaid — so the loudest confirmation in the app told a brand-new
        // arrival everything was fine. Still green, still the word "saved",
        // because it IS saved and this entry really does follow them into the
        // first pool they join. It just says what's missing.
        // Falls back to the whole list because the very first save is the one
        // that creates the entry — before it there is no active sheet to ask,
        // and `every` on an empty list is true, which is the right answer.
        const poolless = active ? active.pool_id == null : entries.every(e => e.pool_id == null);
        setSaveMsg(skipped.length
          ? `Saved. Not counted in ${skipped.map(p => p.pool_name).join(', ')}, unpaid.`
          : poolless
            ? "Picks saved. They'll count once you're in a pool." + clearedNote
            : 'Picks saved!' + clearedNote);
        // Longer for the poolless line: it's asking them to go do something.
        setTimeout(() => setSaveMsg(''), poolless ? 7000 : 4000);
        refreshEntries();
      }
    } catch (e) {
      setSaveMsg(e.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  };

  const tiebreakerGame = games.find(g => g.is_tiebreaker);
  const regularGames = games.filter(g => !g.is_tiebreaker);
  // Every game counts toward the record, tiebreaker included, so progress is
  // out of all of them — not the six that used to be the only scoring ones.
  const pickedCount = games.filter(g => myPicks[g.id]?.team).length;
  const pickableCount = games.length;
  const gamesByDay = groupByDay(regularGames);
  const isLocked = week?.picks_locked === 1;

  // The tiebreaker's score prediction is required whenever its winner is picked.
  // Standings break ties on closest-without-going-over, so a blank number isn't
  // an abstention — it ranks you behind everyone who guessed at all. In the beta
  // people simply missed the field, and it moved final placings.
  //
  // Scoped to "picked the tiebreaker", so filling in Thursday's games on Thursday
  // is unaffected, and it stops mattering once the game locks and the field goes.
  const tbScoreValue = String(tbScore).trim();
  const tbScoreOk = tbScoreValue !== '' && Number.isInteger(Number(tbScoreValue))
                    && Number(tbScoreValue) >= 0 && Number(tbScoreValue) <= 200;
  const tbNeedsScore = !!tiebreakerGame
    && !isLocked
    && tiebreakerGame.status === 'scheduled'
    && !!myPicks[tiebreakerGame.id]?.team
    && !tbScoreOk;

  if (loading) return (
    <div style={{ padding:'80px 20px', textAlign:'center' }}>
      <div style={{ fontSize:40, marginBottom:12 }}>🏈</div>
      <div style={{ color:'#556', fontFamily:"'Oswald',sans-serif", fontSize:16, letterSpacing:1 }}>LOADING GAMES...</div>
    </div>
  );
  if (error) return (
    <div style={{ padding:'80px 20px', textAlign:'center' }}>
      <div style={{ color:'#ff6666', marginBottom:8 }}>{error}</div>
      <div style={{ color:'#445', fontSize:13 }}>Ask your admin to sync the schedule first.</div>
    </div>
  );
  if (!week || games.length === 0) return (
    <div style={{ padding:'60px 20px', textAlign:'center' }}>
      <div style={{ fontSize:56, marginBottom:16 }}>🏈</div>
      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:22, color:'#556', marginBottom:8 }}>NO GAMES LOADED YET</div>
      <div style={{ color:'#445', fontSize:14 }}>
        {user && user.is_admin ? 'Go to Admin and sync the schedule.' : 'Check back soon. The admin needs to sync the schedule.'}
      </div>
    </div>
  );

  return (
    <div style={{ padding:'24px 16px', maxWidth:860, margin:'0 auto' }}>
      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .conf-btn:hover { filter: brightness(1.2); }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ display:'inline-block', background:'linear-gradient(135deg,#013369,#0a5299)', padding:'5px 16px', borderRadius:20, fontSize:12, fontWeight:700, color:'#7fb3ff', marginBottom:10, letterSpacing:1 }}>
          🏈 {week.label}
        </div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:28, fontWeight:700, color:'#fff', marginBottom:4 }}>Make Your Picks</div>
        <div style={{ color:'#667', fontSize:14 }}>Select winners, set confidence, and add your trash talk.</div>
      </div>

      {isLocked && (
        <div style={{ background:'rgba(213,10,10,0.1)', border:'1px solid rgba(213,10,10,0.3)', borderRadius:10, padding:'14px 18px', marginBottom:20, color:'#ff8888', fontWeight:600, fontSize:14 }}>
          🔒 Picks are locked for this week. Good luck!
        </div>
      )}

      {!isLocked && week && <CountdownBar lockAt={week.lock_at} isLocked={isLocked} />}

      <PaywallBanner membership={membership} onNavigate={onNavigate} />

      {/* Entry switcher — only worth the space once there's more than one sheet
          to fill in. Each seat scores in exactly one pool, so a member in three
          pools can back three different teams if they want to. */}
      {entries.length > 1 && (
        <div style={{ marginBottom: 20, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: '#667', fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>
            WHICH SHEET ARE YOU FILLING IN?
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {entries.map(e => {
              const on = e.entry_id === entryId;
              const done = e.weekPicks > 0;
              return (
                <button
                  key={e.entry_id}
                  onClick={() => switchEntry(e.entry_id)}
                  title={e.canSubmit ? undefined : 'This pool needs a season membership before you can save picks'}
                  style={{
                    padding: '8px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                    border: '1px solid ' + (on ? 'rgba(127,179,255,0.55)' : 'rgba(255,255,255,0.1)'),
                    background: on ? 'rgba(127,179,255,0.12)' : 'rgba(255,255,255,0.04)',
                    color: on ? '#cfe4ff' : '#aab', fontSize: 13, fontWeight: 600,
                    opacity: e.canSubmit ? 1 : 0.65
                  }}
                >
                  <div>{e.pool_name || 'No pool yet'}{e.entry_no > 1 ? ` · entry ${e.entry_no}` : ''}</div>
                  <div style={{ fontSize: 11, color: e.canSubmit ? '#778' : '#ffb347', marginTop: 2 }}>
                    {!e.canSubmit ? '🔒 membership needed'
                      : done ? `${e.weekPicks}/${pickableCount} picked` : 'not started'}
                  </div>
                </button>
              );
            })}
          </div>
          {/* Copy across, so three pools isn't three identical sheets by hand. */}
          {entries.filter(e => e.entry_id !== entryId && e.weekPicks > 0).length > 0 && !isLocked && active?.canSubmit && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#667' }}>Copy this week from:</span>
              {entries.filter(e => e.entry_id !== entryId && e.weekPicks > 0).map(e => (
                <button
                  key={e.entry_id}
                  disabled={copying}
                  onClick={() => copyFrom(e.entry_id)}
                  style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)',
                           background: 'rgba(255,255,255,0.05)', color: '#cdd', fontSize: 12,
                           fontWeight: 600, cursor: copying ? 'wait' : 'pointer' }}
                >{e.pool_name || 'No pool'}{e.entry_no > 1 ? ` · ${e.entry_no}` : ''}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unsaved work is worth nothing at kickoff, so say so — and say it louder
          the closer the deadline gets. Without this, clearing a pick by accident
          and wandering off looked identical to having saved. */}
      {!isLocked && pending.dirty && (
        <div style={{
          marginBottom: 16, borderRadius: 10, padding: '12px 16px',
          border: '1px solid ' + (urgent ? 'rgba(213,10,10,0.55)' : 'rgba(255,160,0,0.4)'),
          background: urgent ? 'rgba(213,10,10,0.14)' : 'rgba(255,160,0,0.09)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          animation: urgent ? 'livePulse 2.5s ease-in-out infinite' : 'none'
        }}>
          <span style={{ fontSize: 18 }}>{urgent ? '🚨' : '⚠️'}</span>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: urgent ? '#ff8888' : '#ffc46b' }}>
              {urgent ? "Your changes aren't saved, and picks lock soon." : 'You have unsaved changes.'}
            </div>
            <div style={{ fontSize: 12.5, color: '#99a', marginTop: 2 }}>
              {[
                pending.changed ? `${pending.changed} pick${pending.changed === 1 ? '' : 's'} changed` : null,
                pending.cleared ? `${pending.cleared} cleared` : null
              ].filter(Boolean).join(' · ')}
              {'. These don\'t count until you save.'}
            </div>
          </div>
          <button
            onClick={savePicks} disabled={saving || locked || tbNeedsScore}
            title={tbNeedsScore ? 'Predict the tiebreaker score first' : undefined}
            style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor: (locked || tbNeedsScore) ? 'not-allowed' : 'pointer',
                     background: (locked || tbNeedsScore) ? '#555' : 'linear-gradient(135deg,#D50A0A,#a00808)', color:'#fff',
                     fontSize:12.5, fontWeight:700, fontFamily:"'Oswald',sans-serif", letterSpacing:0.5 }}
          >{saving ? 'SAVING…' : 'SAVE NOW'}</button>
          <button
            onClick={() => { setMyPicks(savedPicks); setSaveMsg('Changes discarded.'); setTimeout(() => setSaveMsg(''), 2500); }}
            disabled={saving}
            title="Put everything back the way it was at your last save"
            style={{ padding:'8px 14px', borderRadius:8, border:'1px solid rgba(255,255,255,0.14)',
                     background:'transparent', color:'#889', fontSize:12, fontWeight:700, cursor:'pointer' }}
          >Discard</button>
        </div>
      )}

      {!isLocked && (
        <div style={{ position:'sticky', top:98, zIndex:50, background:'rgba(10,14,26,0.97)', backdropFilter:'blur(10px)', borderBottom:'1px solid rgba(255,255,255,0.08)', padding:'12px 0', marginBottom:24, display:'flex', alignItems:'center', gap:16 }}>
          <button
            title={locked ? 'A season membership is required to save picks'
                 : tbNeedsScore ? 'Predict the tiebreaker score first' : undefined}
            style={{ padding:'10px 28px', background: (saving || locked || tbNeedsScore) ? '#555' : 'linear-gradient(135deg,#D50A0A,#a00808)', border:'none', borderRadius:8, color:'#fff', fontSize:14, fontWeight:700, cursor: (locked || tbNeedsScore) ? 'not-allowed' : 'pointer', fontFamily:"'Oswald',sans-serif", letterSpacing:1, opacity: (locked || tbNeedsScore) ? 0.6 : 1, boxShadow: pending.dirty && !locked && !tbNeedsScore ? '0 0 0 2px rgba(255,160,0,0.5)' : 'none' }}
            onClick={savePicks} disabled={saving || locked || tbNeedsScore}
          >{saving ? 'Saving...' : locked ? '🔒 MEMBERSHIP REQUIRED' : pending.dirty ? 'SAVE PICKS •' : 'SAVE PICKS'}</button>
          {/* Says why the button is dead, next to the button. A greyed-out save
              with no explanation is the thing people file bug reports about. */}
          {/* Kept short because this sits beside the button in a flex row, and on
              a phone the longer sentence wrapped to four cramped lines. The full
              explanation lives on the tiebreaker card, next to the field itself. */}
          {tbNeedsScore && !saveMsg && (
            <span style={{ fontSize:13, fontWeight:600, color:'#ffa726' }}>
              ⚠️ Tiebreaker score required
            </span>
          )}
          <span style={{ fontSize:13, fontWeight:600, color: saveWarn ? '#ffa726' : saveMsg.includes('saved') ? '#4caf50' : '#ff6666' }}>{saveMsg}</span>
          <div style={{ flex:1 }} />
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ height:6, width:120, background:'rgba(255,255,255,0.1)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', background:'linear-gradient(90deg,#013369,#D50A0A)', borderRadius:3, transition:'width 0.3s', width:((pickedCount/Math.max(pickableCount,1))*100)+'%' }} />
            </div>
            <span style={{ fontSize:13, color:'#889', whiteSpace:'nowrap' }}>{pickedCount}/{pickableCount}</span>
          </div>
        </div>
      )}

      {Object.entries(gamesByDay).map(([day, dayGames]) => (
        <div key={day} style={{ marginBottom:28 }}>
          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, fontWeight:700, color:'#7fb3ff', letterSpacing:2, textTransform:'uppercase', marginBottom:12, paddingBottom:8, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
            {day}
          </div>
          {dayGames.map(function(game) {
            const pick = myPicks[game.id] || {};
            const myTeam = pick.team;
            const isFinal = game.status === 'final';
            const isLive = game.status === 'in_progress';
            const gameLockedNow = isGameLockedNow(game, isLocked);
            const canPick = !gameLockedNow && game.status === 'scheduled';
            const missedPick = gameLockedNow && !myTeam && !isFinal && !isLive;
            const awayColor = TEAM_COLORS[game.away_abbr] || '#555';
            const homeColor = TEAM_COLORS[game.home_abbr] || '#555';
            /*
             * Which side did they take, and can they tell at a glance?
             *
             * "Picked" used to be shown only as a 16%-alpha wash of the team's
             * OWN colour plus an 8px dot in that same colour. For a dark team
             * that is invisible: Bears is #0B162A and Browns #311D00, both
             * nearly black on a #0d1525 card, so a Bears-Browns game gave no
             * usable signal about which one you had taken.
             *
             * So the signal no longer depends on the team's colour at all — a
             * fixed blue ring and a PICK label, with the team tint kept only as
             * flavour on top. The other side dims, because the contrast between
             * the two is what the eye actually reads.
             */
            const pickedAway = myTeam === game.away_abbr;
            const pickedHome = myTeam === game.home_abbr;
            // A tie stores winner_abbr as the literal 'TIE', which equals
            // neither side. Left alone, every "did this side win" test below
            // answers no for both — dimming the whole card and stamping a ❌ on
            // whichever team you took, for a game nobody could have won.
            const tied = isFinal && game.winner_abbr === 'TIE';
            const sideOpacity = (isThisSide) => {
              if (tied) return 1;
              if (isFinal) return game.winner_abbr === (isThisSide === 'away' ? game.away_abbr : game.home_abbr) ? 1 : 0.4;
              if (!myTeam) return 1;
              return (isThisSide === 'away' ? pickedAway : pickedHome) ? 1 : 0.45;
            };
            const pickLabel = <PickChip side="away" />;
            const pickLabelLeft = <PickChip side="home" />;
            const ss = getStatusStyle(game.status, gameLockedNow, !!myTeam);

            return (
              <div key={game.id} style={{ background:'#0d1525', border:'1px solid ' + (missedPick ? 'rgba(213,10,10,0.4)' : myTeam ? (TEAM_COLORS[myTeam]||'#555')+'55' : 'rgba(255,255,255,0.07)'), borderRadius:14, marginBottom:14, overflow:'hidden', boxShadow: missedPick ? '0 2px 16px rgba(213,10,10,0.2)' : myTeam ? '0 2px 16px ' + (TEAM_COLORS[myTeam]||'#555') + '22' : '0 2px 10px rgba(0,0,0,0.3)', transition:'border-color 0.2s' }}>

                {/* Game header */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 16px', background:'rgba(0,0,0,0.2)', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize:12, color:'#556', fontWeight:600 }}>
                    {game.game_date ? game.game_date + ' · ' : ''}{game.game_time}
                  </span>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:12, letterSpacing:0.5, background:ss.bg, color:ss.color, animation: isLive ? 'livePulse 2s ease-in-out infinite' : 'none' }}>
                    {getStatusLabel(game.status, gameLockedNow, !!myTeam)}
                    {isLive && game.status_detail ? ' · ' + game.status_detail : ''}
                  </span>
                </div>

                {/* Why that tap did nothing. On the card, because the save
                    button it would otherwise appear next to is often off
                    screen — and a tap that silently does nothing reads as the
                    app being broken rather than the game being shut. */}
                {lockNote && lockNote.gameId === game.id && (
                  <div style={{
                    padding: '8px 16px', background: 'rgba(255,160,0,0.10)',
                    borderBottom: '1px solid rgba(255,160,0,0.25)',
                    fontSize: 12.5, color: '#ffa726', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 8
                  }}>
                    <span>🔒</span><span>{lockNote.text}</span>
                  </div>
                )}

                {/* Matchup row */}
                <div style={{ display:'flex', alignItems:'stretch' }}>
                  {/* Away team button */}
                  <button
                    onClick={() => pickTeam(game.id, game.away_abbr, gameLockedNow, game.status)}
                    style={{ flex:1, padding:'20px 16px', background: pickedAway ? pickedBackground(awayColor) : 'transparent', border:'none', borderRight:'1px solid rgba(255,255,255,0.05)', cursor: canPick ? 'pointer' : 'default', display:'flex', flexDirection:'column', alignItems:'center', gap:10, position:'relative', transition:'background 0.15s, box-shadow 0.15s', opacity: sideOpacity('away'), boxShadow: pickedAway ? `inset 0 0 0 2px ${PICK_RING}` : 'none' }}
                  >
                    {(isFinal || isLive) && (
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:28, fontWeight:700, color: isFinal && (tied || game.winner_abbr === game.away_abbr) ? '#fff' : '#667', position:'absolute', top:12, left:16 }}>{game.away_score}</div>
                    )}
                    <TeamLogo abbr={game.away_abbr} size={56} dim={isFinal && !tied && game.winner_abbr !== game.away_abbr} />
                    <div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color: myTeam === game.away_abbr ? '#fff' : '#ccd', letterSpacing:0.5 }}>{game.away_abbr}</div>
                      {game.away_record && <div style={{ fontSize:11, color:'#556', textAlign:'center' }}>{game.away_record}</div>}
                      <div style={{ fontSize:11, color:'#445', textAlign:'center', marginTop:2 }}>AWAY</div>
                    </div>
                    {pickedAway && pickLabel}
                    {isFinal && myTeam === game.away_abbr && (
                      <div style={{ position:'absolute', bottom:8, right:8, fontSize:16 }}>{tied ? '🤝' : game.winner_abbr === game.away_abbr ? '✅' : '❌'}</div>
                    )}
                  </button>

                  {/* VS divider */}
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'0 12px', minWidth:40, color:'#334', fontWeight:700, fontSize:13 }}>@</div>

                  {/* Home team button */}
                  <button
                    onClick={() => pickTeam(game.id, game.home_abbr, gameLockedNow, game.status)}
                    style={{ flex:1, padding:'20px 16px', background: pickedHome ? pickedBackground(homeColor) : 'transparent', border:'none', borderLeft:'1px solid rgba(255,255,255,0.05)', cursor: canPick ? 'pointer' : 'default', display:'flex', flexDirection:'column', alignItems:'center', gap:10, position:'relative', transition:'background 0.15s, box-shadow 0.15s', opacity: sideOpacity('home'), boxShadow: pickedHome ? `inset 0 0 0 2px ${PICK_RING}` : 'none' }}
                  >
                    {(isFinal || isLive) && (
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:28, fontWeight:700, color: isFinal && (tied || game.winner_abbr === game.home_abbr) ? '#fff' : '#667', position:'absolute', top:12, right:16 }}>{game.home_score}</div>
                    )}
                    <TeamLogo abbr={game.home_abbr} size={56} dim={isFinal && !tied && game.winner_abbr !== game.home_abbr} />
                    <div>
                      <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color: myTeam === game.home_abbr ? '#fff' : '#ccd', letterSpacing:0.5 }}>{game.home_abbr}</div>
                      {game.home_record && <div style={{ fontSize:11, color:'#556', textAlign:'center' }}>{game.home_record}</div>}
                      <div style={{ fontSize:11, color:'#445', textAlign:'center', marginTop:2 }}>HOME</div>
                    </div>
                    {pickedHome && pickLabelLeft}
                    {isFinal && myTeam === game.home_abbr && (
                      <div style={{ position:'absolute', bottom:8, left:8, fontSize:16 }}>{tied ? '🤝' : game.winner_abbr === game.home_abbr ? '✅' : '❌'}</div>
                    )}
                  </button>
                </div>

                <BriefingStrip
                  game={game}
                  brief={briefings[game.id]}
                  show={canPick}
                  open={!!openBriefs[game.id]}
                  onToggle={() => setOpenBriefs(prev => ({ ...prev, [game.id]: !prev[game.id] }))}
                />

                {/* Where you stand on this game: how your pick is faring while
                    it's live, and how the pool split it once that's public.
                    Both come from data already on the page — no extra calls. */}
                {(() => {
                  const live = livePickState(game, myTeam);
                  const split = splitLine(splits[game.id], myTeam);
                  if (!live && !split) return null;
                  return (
                    <div style={{ padding:'8px 16px', borderTop:'1px solid rgba(255,255,255,0.05)', background:'rgba(0,0,0,0.18)', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                      {live && (
                        <span style={{ fontSize:12.5, fontWeight:700, color:live.color }}>{live.text}</span>
                      )}
                      {live && split && <span style={{ color:'#334', fontSize:11 }}>|</span>}
                      {split && (
                        <span style={{ fontSize:12, color:'#778' }}>{split}</span>
                      )}
                    </div>
                  );
                })()}

                {/* Confidence + comment (show when pick is made and not locked/final) */}
                {myTeam && canPick && (
                  <div style={{ padding:'12px 16px 14px', borderTop:'1px solid rgba(255,255,255,0.05)', background:'rgba(0,0,0,0.15)' }}>
                    {/* Wraps on purpose. Low, Medium, High and Clear at a real
                        44px each do not fit beside the label on a 375px screen,
                        and shrinking them back to fit is how they got to 22px in
                        the first place. */}
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', rowGap:6, marginBottom: pick.comment !== undefined ? 10 : 0 }}>
                      <span style={{ fontSize:11.5, color:'#667', fontWeight:700, letterSpacing:0.8, marginRight:4 }}>CONFIDENCE</span>
                      {CONFIDENCE.map(c => (
                        <button
                          key={c}
                          className="conf-btn"
                          onClick={() => setConfidence(game.id, pick.confidence === c ? null : c)}
                          style={{ minHeight:44, minWidth:44, padding:'0 14px', borderRadius:8, border:'1px solid ' + (pick.confidence === c ? CONF_COLOR[c] : 'rgba(255,255,255,0.1)'), background: pick.confidence === c ? CONF_COLOR[c]+'22' : 'transparent', color: pick.confidence === c ? CONF_COLOR[c] : '#667', fontSize:12.5, fontWeight:700, cursor:'pointer', transition:'all 0.15s', letterSpacing:0.5 }}
                        >{CONF_LABEL[c]}</button>
                      ))}
                      <div style={{ flex:1 }} />
                      {/* Sit this one out. Available only while the game is still
                          unlocked — the same rule that stops a pick being switched
                          after kickoff stops it being withdrawn. */}
                      <button
                        onClick={() => clearPick(game.id)}
                        title="Remove your pick for this game"
                        style={{ minHeight:44, minWidth:44, padding:'0 12px', borderRadius:8, border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#778', fontSize:11.5, fontWeight:700, cursor:'pointer', letterSpacing:0.5 }}
                      >✕ CLEAR</button>
                    </div>
                    <input
                      placeholder="Why this pick? (optional trash talk...)"
                      value={pick.comment || ''}
                      onChange={e => setComment(game.id, e.target.value)}
                      maxLength={150}
                      style={{ width:'100%', padding:'8px 12px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:7, color:'#ccd', fontSize:13, outline:'none', boxSizing:'border-box' }}
                    />
                  </div>
                )}

                {/* Show saved confidence/comment after lock */}
                {myTeam && (isFinal || isLocked) && (pick.confidence || pick.comment) && (
                  <div style={{ padding:'8px 16px 10px', borderTop:'1px solid rgba(255,255,255,0.05)', background:'rgba(0,0,0,0.15)', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                    {pick.confidence && (
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:12, background:CONF_COLOR[pick.confidence]+'22', color:CONF_COLOR[pick.confidence], letterSpacing:0.5 }}>{CONF_LABEL[pick.confidence].toUpperCase()} CONFIDENCE</span>
                    )}
                    {pick.comment && (
                      <span style={{ fontSize:12, color:'#778', fontStyle:'italic' }}>"{pick.comment}"</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Tiebreaker */}
      {tiebreakerGame && (() => {
        // The same pick treatment the regular cards get. This block draws its
        // own buttons, so it has to say so itself.
        const tbTeam    = myPicks[tiebreakerGame.id]?.team;
        const tbAway    = tbTeam === tiebreakerGame.away_abbr;
        const tbHome    = tbTeam === tiebreakerGame.home_abbr;
        const tbFinal   = tiebreakerGame.status === 'final';
        // See `tied` on the regular cards — 'TIE' matches neither abbreviation,
        // so without this both sides of a drawn tiebreaker dim to 0.4.
        const tbTied    = tbFinal && tiebreakerGame.winner_abbr === 'TIE';
        const tbOpacity = (side) => {
          const abbr = side === 'away' ? tiebreakerGame.away_abbr : tiebreakerGame.home_abbr;
          if (tbTied) return 1;
          if (tbFinal) return tiebreakerGame.winner_abbr === abbr ? 1 : 0.4;
          if (!tbTeam) return 1;
          return (side === 'away' ? tbAway : tbHome) ? 1 : 0.45;
        };
        const tbSide = (side) => {
          const picked = side === 'away' ? tbAway : tbHome;
          const abbr   = side === 'away' ? tiebreakerGame.away_abbr : tiebreakerGame.home_abbr;
          return {
            flex:1, padding:'20px 16px', border:'none', position:'relative',
            background: picked ? pickedBackground(TEAM_COLORS[abbr] || '#555') : 'transparent',
            boxShadow: picked ? `inset 0 0 0 2px ${PICK_RING}` : 'none',
            opacity: tbOpacity(side),
            [side === 'away' ? 'borderRight' : 'borderLeft']: '1px solid rgba(255,255,255,0.06)',
            cursor: (!isLocked && tiebreakerGame.status === 'scheduled') ? 'pointer' : 'default',
            display:'flex', flexDirection:'column', alignItems:'center', gap:10,
            transition:'background 0.15s, box-shadow 0.15s'
          };
        };
        // Not always Monday night — the tiebreaker is the last game of the week,
        // whatever day that falls on. In preseason it usually isn't a Monday.
        const tbWhen = tiebreakerGame.game_date ? `: ${tiebreakerGame.game_date.toUpperCase()}` : '';

        return (
        <div style={{ background:'linear-gradient(135deg,rgba(1,51,105,0.3),rgba(10,82,153,0.15))', border:'2px solid rgba(1,51,105,0.5)', borderRadius:14, padding:'20px', marginTop:8 }}>
          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color:'#7fb3ff', marginBottom:4, letterSpacing:1 }}>
            TIEBREAKER{tbWhen}
          </div>
          <div style={{ fontSize:13, color:'#667', marginBottom:16 }}>
            Last game of the week. Pick the winner AND predict the combined final score. Closest without going over wins ties.
          </div>

          <div style={{ display:'flex', alignItems:'stretch', background:'#0d1525', borderRadius:12, overflow:'hidden', border:'1px solid rgba(127,179,255,0.15)' }}>
            <button
              onClick={() => pickTeam(tiebreakerGame.id, tiebreakerGame.away_abbr, isLocked, tiebreakerGame.status)}
              style={tbSide('away')}
            >
              {tbAway && <PickChip side="away" />}
              {(tiebreakerGame.status === 'final' || tiebreakerGame.status === 'in_progress') && (
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:28, fontWeight:700, color:'#667' }}>{tiebreakerGame.away_score}</div>
              )}
              <TeamLogo abbr={tiebreakerGame.away_abbr} size={52} />
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color:'#ccd' }}>{tiebreakerGame.away_abbr}</div>
              {tiebreakerGame.away_record && <div style={{ fontSize:11, color:'#556' }}>{tiebreakerGame.away_record}</div>}
            </button>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'0 12px', color:'#334', fontWeight:700, fontSize:13 }}>@</div>

            <button
              onClick={() => pickTeam(tiebreakerGame.id, tiebreakerGame.home_abbr, isLocked, tiebreakerGame.status)}
              style={tbSide('home')}
            >
              {tbHome && <PickChip side="home" />}
              {(tiebreakerGame.status === 'final' || tiebreakerGame.status === 'in_progress') && (
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:28, fontWeight:700, color:'#667' }}>{tiebreakerGame.home_score}</div>
              )}
              <TeamLogo abbr={tiebreakerGame.home_abbr} size={52} />
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color:'#ccd' }}>{tiebreakerGame.home_abbr}</div>
              {tiebreakerGame.home_record && <div style={{ fontSize:11, color:'#556' }}>{tiebreakerGame.home_record}</div>}
            </button>
          </div>

          {/* Directly above the box asking for a combined score, because the
              over/under IS the market's combined-score prediction and this is
              the one game where that number is the answer to the question being
              asked. Wrapped to sit inside the tiebreaker's own panel rather than
              spanning it like a card row. */}
          <div style={{ marginTop: 12, borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
            <BriefingStrip
              game={tiebreakerGame}
              brief={briefings[tiebreakerGame.id]}
              show={!isLocked && tiebreakerGame.status === 'scheduled'}
              open={!!openBriefs[tiebreakerGame.id]}
              onToggle={() => setOpenBriefs(prev => ({ ...prev, [tiebreakerGame.id]: !prev[tiebreakerGame.id] }))}
            />
          </div>

          {!isLocked && tiebreakerGame.status === 'scheduled' && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:13, color: tbNeedsScore ? '#ffa726' : '#7fb3ff', fontWeight:600, marginBottom:8 }}>
                Combined final score prediction:
                <span style={{ color: tbNeedsScore ? '#ffa726' : '#667', fontWeight:700, marginLeft:6 }}>
                  {tbNeedsScore ? '· REQUIRED' : '· required'}
                </span>
              </div>
              <input
                type="number" min="0" max="200" placeholder="e.g. 47"
                value={tbScore}
                onChange={e => {
                  setTbScore(e.target.value);
                  if (tiebreakerGame) {
                    // Same reason as the payload: `|| null` would treat a
                    // predicted 0 as no prediction at all.
                    const v = e.target.value.trim();
                    setMyPicks(prev => ({ ...prev, [tiebreakerGame.id]: { ...(prev[tiebreakerGame.id]||{}), tiebreaker: v === '' ? null : parseInt(v, 10) } }));
                  }
                }}
                style={{ padding:'10px 14px', background:'rgba(255,255,255,0.08)', border:'1px solid ' + (tbNeedsScore ? 'rgba(255,167,38,0.75)' : 'rgba(255,255,255,0.2)'), borderRadius:8, color:'#fff', fontSize:22, fontWeight:700, fontFamily:"'Oswald',sans-serif", width:120, textAlign:'center', outline:'none', boxShadow: tbNeedsScore ? '0 0 0 2px rgba(255,167,38,0.25)' : 'none' }}
              />
              {tbNeedsScore && (
                <div style={{ fontSize:12.5, color:'#ffa726', marginTop:8 }}>
                  You've picked a winner here. This number decides ties, so it has to be filled in before your picks can save.
                </div>
              )}
            </div>
          )}

          {tiebreakerGame.status === 'final' && (
            <div style={{               marginTop:12, padding:'10px 14px', background:'rgba(0,0,0,0.3)', borderRadius:8 }}>
              <span style={{ color:'#aab', fontSize:13 }}>
                Final: {tiebreakerGame.away_abbr} {tiebreakerGame.away_score} to {tiebreakerGame.home_abbr} {tiebreakerGame.home_score}
                &nbsp;|&nbsp; Total: <strong style={{ color:'#7fb3ff' }}>{tiebreakerGame.home_score + tiebreakerGame.away_score}</strong>
                {myPicks[tiebreakerGame.id] && myPicks[tiebreakerGame.id].tiebreaker && (
                  <> &nbsp;|&nbsp; Your guess: <strong>{myPicks[tiebreakerGame.id].tiebreaker}</strong>
                  &nbsp;(off by {Math.abs((myPicks[tiebreakerGame.id].tiebreaker||0)-(tiebreakerGame.home_score+tiebreakerGame.away_score))})</>
                )}
              </span>
            </div>
          )}
        </div>
        );
      })()}
      <div style={{ height:40 }} />
    </div>
  );
}
